import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import { GlobalToken, theme } from 'antd';

import { SpinCentered } from '@/comp/index';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import Super from '@/comp/Super';
import { CustomThemeToken } from '@/theme';

import IconInMaintenance from '@/assets/icon-in-maintenance.svg?react';
import PowerTooltip from './Tooltip/PowerTooltip';
import VolumeTooltip from './Tooltip/VolumeTooltip';
import getYearlyhGraphInitOptions from '../YearlyhGraphInitOptions';

const { useToken } = theme;

const PowerTab = observer(({ yearToPresent, animation }: { yearToPresent: number; animation?: boolean }) => {
  const { energySavingsGraphStore, energySavingsStore, userStore } = useStores();
  const { t } = useTranslation();
  const { token } = useToken() as unknown as CustomThemeToken;
  const [options, setOptions] = useState(getYearlyhGraphInitOptions(t, token));
  const { tunit } = useMeasurement();

  const categories = () => {
    const categories = [];
    const dateStart = moment().month(0);
    const dateEnd = moment().month(12);

    categories.push('0');
    while (dateEnd.diff(dateStart, 'months') > 0) {
      categories.push(dateStart.format('MMM'));
      dateStart.add(1, 'month');
    }

    return categories;
  };

  useEffect(() => {
    const newOptions = {
      ...getYearlyhGraphInitOptions(t, token),
      xAxis: {
        animation,
        lineWidth: 2,
        lineColor: token.colorBgContainer,
        tickColor: token.colorBgContainer,
        showEmpty: true,
        labels: {
          formatter: function () {
            if (yearToPresent === moment().year() && this.pos === moment().month() + 1) {
              return `${t(this.value)}<span style="fill: ${token.colorError};"> *</span>`;
            } else {
              return t(this.value);
            }
          },
          style: {
            fontSize: '7.7px',
            color: token.colorTextSecondary,
          },
        },
        categories: categories(),

        type: 'category',
        min: 1,
        max: 12,
      },
      yAxis: [
        // yAxis 1
        {
          animation,
          gridLineColor: token.colorBorderSecondary,
          lineColor: token.colorBorderSecondary,
          gridLineDashStyle: 'longdash',
          lineWidth: 2,
          title: {
            text: 'kWh',
            align: 'high',
            rotation: 0,
            y: -15,
            offset: -11,
            style: {
              fontSize: '9.6px',
              color: token.colorTextSecondary,
            },
          },
          labels: {
            style: {
              fontSize: '9.6px',
              color: token.colorTextSecondary,
            },
            x: -9,
          },
          min: 0,
        },
        // yAxis 2
        {
          animation,
          gridLineColor: token.colorBorderSecondary,
          lineColor: token.colorBorderSecondary,
          gridLineDashStyle: 'longdash',
          lineWidth: 2,
          visible: false,
          title: {
            useHTML: true,
            text: ReactDOMServer.renderToString(
              <Trans i18nKey={tunit('m^3')}>
                <Super />
              </Trans>,
            ),
            align: 'high',
            rotation: 0,
            y: -25,
            offset: -11,
            style: {
              fontSize: '12px',
              color: token.colorTextSecondary,
            },
          },
          labels: {
            style: {
              fontSize: '9.6px',
              color: token.colorTextSecondary,
            },
          },
          opposite: true,
          min: 0,
        },
      ],
      tooltip: {
        shared: false,
        shadow: false,
        shape: 'square',
        useHTML: true,
        padding: 0,
        backgroundColor: undefined,
        borderWidth: 0,
        borderRadius: 0,
        formatter: function () {
          switch (this.series.name) {
            case t('Volume'):
              return ReactDOMServer.renderToString(<VolumeTooltip value={this.y} token={token} />);

            case t('Actual Energy'):
              return ReactDOMServer.renderToString(
                <PowerTooltip
                  value={this.y}
                  specificPower={this.point.specificPower}
                  volume={this.point.volume}
                  name={this.series.name}
                  measurementSystem={userStore?.user?.measurementSystem}
                  energySaving={this.point.energySaving}
                  co2Saving={this.point.co2Saving}
                  kWhToMoneyRate={energySavingsStore.energySavings?.monthSavings?.kWhToMoneyRate}
                  savingCurrency={energySavingsStore.energySavings?.monthSavings?.savingCurrency}
                  token={token}
                />,
              );

            case t('Baseline'):
              return ReactDOMServer.renderToString(
                <PowerTooltip
                  value={this.y}
                  specificPower={this.point.specificPower}
                  volume={this.point.volume}
                  name={this.series.name + ' Simulation'}
                  measurementSystem={userStore?.user?.measurementSystem}
                  token={token}
                />,
              );
          }
          return ReactDOMServer.renderToString(<VolumeTooltip value={this.y} token={token} />);
        },
      },
      plotOptions: {
        column: {
          animation,
          grouping: false,
          shadow: false,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            crop: false,
            overflow: 'none',
            useHTML: true,
            formatter: function () {
              return this.point.isInMaintenance ? ReactDOMServer.renderToString(<IconInMaintenance />) : null;
            },
          },
        },
      },
      series: [
        {
          name: t('Baseline'),
          legendIndex: 1,
          color: token.Ecoplant_Charts.colorBaseline,
          data: energySavingsGraphStore.energySavingsGraphs?.baselineSeries,
          pointPadding: 0.25,
          events: {
            legendItemClick: function (e) {
              e.preventDefault();
            },
          },
          zIndex: 1,
        },
        {
          name: t('Actual Energy'),
          legendIndex: 0,
          color: token.Ecoplant_Charts.colorActualenergy,
          data: energySavingsGraphStore.energySavingsGraphs?.actualEnergySeries,
          pointPadding: 0.4,
          events: {
            legendItemClick: function (e) {
              e.preventDefault();
            },
          },
          zIndex: 2,
        },
        {
          name: t('Volume'),
          legendIndex: 2,
          type: 'line',
          color: token.Ecoplant_Charts.colorSystemProductionfloorProduction,
          yAxis: 1,
          visible: false,
          marker: {
            enabled: true,
          },
          events: {
            legendItemClick: function () {
              const isVolumeVisible = !this.chart.yAxis[1].visible;
              this.chart.yAxis[1].update({ visible: isVolumeVisible, title: { enabled: isVolumeVisible } });
            },
          },
          data: energySavingsGraphStore.energySavingsGraphs?.volumeSeries,
          zIndex: 3,
        },
        {
          name: t('Maintenance Event'),
          legendIndex: 3,
          type: 'scatter',
          marker: {
            symbol: `url(${IconInMaintenance})`,
          },
          events: {
            legendItemClick: function (e) {
              e.preventDefault();
            },
            mouseOut: function (e) {
              e.preventDefault();
            },
            mouseOver: function (e) {
              e.preventDefault();
            },
            checkboxClick: function (e) {
              e.preventDefault();
            },
          },
        },
      ],
    };

    setOptions(newOptions);
  }, [energySavingsGraphStore.energySavingsGraphs, token, t]);

  return (
    <Container>
      {energySavingsGraphStore.isLoading ? (
        <SpinCentered className={'spinner'} />
      ) : (
        <div className="vd-energy-savings-power-content">
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>
      )}
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;

  .asterisk {
    color: ${({ theme: { token } }) => token.colorError};
    font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
    position: absolute;
    right: 165px;
    top: 930px;
  }
`;

export default PowerTab;
